<script lang="ts">
  import { onMount } from 'svelte';
  import type { Channel } from '@multiview/shared';
  import { api, type Station } from '../lib/api/client.ts';

  /**
   * Which of your channels each broadcaster in FotMob's TV listings is. The
   * automatic name matching is right for most stations; this is where the rest
   * gets fixed once instead of every matchday.
   */
  let stations = $state<Station[]>([]);
  let selectedId = $state<string | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let saving = $state(false);

  let query = $state('');
  let results = $state<Channel[]>([]);

  const selected = $derived(stations.find((s) => s.stationId === selectedId) ?? null);

  async function load() {
    loading = true;
    try {
      stations = (await api.stations()).stations;
      if (!selectedId && stations.length > 0) selectedId = stations[0]!.stationId;
      error = null;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  }

  async function reloadListings() {
    loading = true;
    try {
      await api.refreshStations();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
    await load();
  }

  onMount(() => { void load(); });

  async function update(patch: { ignored?: boolean; channelIds?: number[] | null }) {
    if (!selected) return;
    saving = true;
    try {
      await api.updateStation(selected.stationId, patch);
      await load();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  /** Adding to an automatic station turns it into your own selection. */
  function assign(channelId: number) {
    if (!selected) return;
    const current = selected.manual ? selected.channels.map((c) => c.channelId) : [];
    if (current.includes(channelId)) return;
    void update({ channelIds: [...current, channelId] });
  }

  function unassign(channelId: number) {
    if (!selected) return;
    const rest = selected.channels.map((c) => c.channelId).filter((id) => id !== channelId);
    // Removing the last one means "back to automatic", not "nothing".
    void update({ channelIds: rest.length > 0 ? rest : null });
  }

  let searchTimer: number | null = null;
  $effect(() => {
    const q = query.trim();
    if (searchTimer !== null) clearTimeout(searchTimer);
    if (q.length < 2) { results = []; return; }
    searchTimer = window.setTimeout(async () => {
      try {
        results = (await api.listChannels({ q, limit: 40 })).channels;
      } catch {
        results = [];
      }
    }, 220);
  });

  const when = (ms: number | null) => (ms
    ? new Date(ms).toLocaleString('de-DE', { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : '');

  const KIND: Record<string, string> = { manual: 'deine Wahl', exact: 'gleicher Name', family: 'Event-Kanal' };
</script>

<div class="stations">
  <section class="panel list scroll">
    <header class="head">
      <h2>Sender <span class="faint">{stations.length}</span></h2>
      <button data-focusable class="plain" disabled={loading} onclick={reloadListings}>
        {loading ? 'lädt …' : 'Neu laden'}
      </button>
    </header>
    <p class="faint small">
      Aus den TV-Listen von FotMob für die nächsten Tage. Grün: ein Kanal steht fest.
    </p>
    {#if error}<p class="error">{error}</p>{/if}
    <ul>
      {#each stations as s, i (s.stationId)}
        {@const certain = !s.ignored && s.channels.some((c) => c.kind !== 'family') && !(s.platform && s.channels.length > 1)}
        <li>
          <button
            class="row"
            data-focusable
            data-focus-default={i === 0 ? true : undefined}
            class:chosen={s.stationId === selectedId}
            class:ignored={s.ignored}
            onclick={() => { selectedId = s.stationId; query = ''; }}
          >
            <span class="name">
              <span class="dot" class:ok={certain} class:maybe={!certain && s.channels.length > 0 && !s.ignored}></span>
              {s.name}
            </span>
            <span class="faint small">
              {s.matches} {s.matches === 1 ? 'Spiel' : 'Spiele'}
              · {s.ignored ? 'ausgeblendet' : s.manual ? 'eigene Zuordnung' : s.channels.length > 0 ? `${s.channels.length} Vorschläge` : 'kein Kanal gefunden'}
            </span>
          </button>
        </li>
      {/each}
    </ul>
  </section>

  <section class="panel detail scroll">
    {#if !selected}
      <p class="faint">{loading ? 'lädt …' : 'Keine Sender in den TV-Listen.'}</p>
    {:else}
      <header>
        <h2>{selected.name}</h2>
        <p class="faint small">
          {selected.type ?? 'Sender'} · {selected.country.toUpperCase()} · {selected.matches} Spiele
          {#if selected.next}· nächstes: {selected.next.homeTeam} – {selected.next.awayTeam} {when(selected.next.start)}{/if}
        </p>
      </header>

      <div class="actions">
        <button data-focusable disabled={saving} onclick={() => update({ ignored: !selected.ignored })}>
          {selected.ignored ? 'Wieder einblenden' : 'Habe ich nicht – ausblenden'}
        </button>
        {#if selected.manual}
          <button data-focusable disabled={saving} onclick={() => update({ channelIds: null })}>Zurück auf automatisch</button>
        {/if}
      </div>

      {#if !selected.ignored}
        <h3>{selected.manual ? 'Deine Kanäle' : 'Automatisch gefunden'}</h3>
        {#if selected.channels.length === 0}
          <p class="faint">Kein Kanal mit passendem Namen. Unten suchen und zuordnen.</p>
        {:else}
          {#if selected.platform}
            <p class="faint small">
              {selected.name} zeigt mehrere Spiele gleichzeitig auf wechselnden Kanälen. Welcher
              es ist, entscheidet das EPG deiner Playlist. Lege hier die Kanäle fest, die du
              tatsächlich hast – dann wird nur unter diesen gesucht.
            </p>
          {/if}
          <ul class="assigned">
            {#each selected.channels as c (c.channelId)}
              <li>
                <span class="cname">{c.name}</span>
                <span class="faint small">{c.group ?? ''} · {KIND[c.kind]}</span>
                {#if selected.manual}
                  <button data-focusable class="icon" aria-label={`${c.name} entfernen`} onclick={() => unassign(c.channelId)}>✕</button>
                {:else}
                  <button data-focusable onclick={() => assign(c.channelId)}>Festlegen</button>
                {/if}
              </li>
            {/each}
          </ul>
        {/if}

        {#if selected.missing.length > 0}
          <p class="warn small">
            Nicht mehr in der Playlist: {selected.missing.map((m) => m.name).join(', ')}
          </p>
        {/if}

        <h3>Anderen Kanal zuordnen</h3>
        <input bind:value={query} data-focusable placeholder="Kanal suchen …" aria-label="Kanal suchen" />
        <ul class="results">
          {#each results as c (c.id)}
            <li>
              <button class="row" data-focusable onclick={() => assign(c.id)}>
                <span class="name">{c.name}</span>
                <span class="faint small">{c.group ?? '—'}</span>
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    {/if}
  </section>
</div>

<style>
  .stations {
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: var(--gap);
  }

  @media (max-width: 900px) {
    .stations { grid-template-columns: 1fr; }
  }

  .panel {
    display: flex;
    flex-direction: column;
    gap: var(--gap);
    min-height: 0;
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: var(--pad);
  }

  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--gap); }
  h2 { margin: 0; font-size: var(--font-lg); }
  h3 { margin: var(--gap) 0 0; font-size: var(--font-base); color: var(--text-dim); }
  p { margin: 0; }
  .small { font-size: var(--font-sm); }
  .plain { background: transparent; font-size: var(--font-sm); }
  .error { color: var(--danger); }
  .warn { color: var(--warn); }

  ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }

  .row {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    text-align: left;
    background: transparent;
    border-color: transparent;
  }
  .row.chosen { border-color: var(--accent); background: rgba(255, 255, 255, 0.04); }
  .row.ignored { opacity: 0.5; }
  .row .name { font-weight: 500; display: flex; align-items: center; gap: 0.5em; }

  .dot { width: 0.6em; height: 0.6em; border-radius: 50%; background: var(--border-strong); flex: 0 0 auto; }
  .dot.ok { background: var(--accent); }
  .dot.maybe { background: var(--warn); }

  .actions { display: flex; gap: var(--gap); flex-wrap: wrap; }

  .assigned li {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: var(--gap);
    align-items: center;
    padding: 4px 0;
    border-bottom: 1px solid var(--border);
  }
  .assigned li .faint { grid-column: 1; grid-row: 2; }
  .assigned li button { grid-row: 1 / span 2; grid-column: 3; font-size: var(--font-sm); }
  .cname { font-weight: 500; }
  .icon { background: transparent; border-color: transparent; }

  .results { max-height: 40vh; overflow-y: auto; }
</style>
